import { Component, Prop, Vue } from 'vue-property-decorator';
import { sortBy } from 'lodash';
import moment from 'moment';
import { DATE_FORMAT, DEFAULT_DISPLAY_VALUE, EVALUATION_STATUSES, PAGE_NAMES } from '@/constants';
import { convertNumberToFourDigitsString } from '@/helpers';
import { UserDTO, ProjectDTO, UserEngagementDTO, DisplayStatus } from '@/models';
import { getUserDisplayInfo, projectTypeMapping } from '@/services';
import Avatar from '@/components/common/avatar/Avatar.vue';
import MemberHoverInfo from '@/components/pages/project-list/member-hover-info/MemberHoverInfo.vue';
import Status from '@/components/common/status/Status.vue';
import ProjectManagerInformation from '../../project-evaluation/project-information/project-manager-information/ProjectManagerInformation.vue';

interface MembersData {
  members: UserEngagementDTO[];
  displayMembers: UserEngagementDTO[];
  tooltip: string;
  numberOfMembers: number;
  numberOfNotDisplayMembers: number;
}

const numberOfDisplayMember = 5;

@Component({
  components: {
    Avatar,
    MemberHoverInfo,
    Status,
    ProjectManagerInformation
  }
})
export default class ProjectCard extends Vue {
  @Prop({ default: {} as ProjectDTO }) private project: ProjectDTO;

  get isEvaluationNotStarted(): boolean {
    return this.project.projectStatus === EVALUATION_STATUSES.NOT_STARTED.label;
  }

  get displayStatus(): DisplayStatus {
    return EVALUATION_STATUSES[this.project.projectStatus];
  }

  get displayID(): string {
    return convertNumberToFourDigitsString(this.project?.id);
  }

  get coManagers(): UserDTO[] {
    return this.project?.coManagers || [];
  }
  get displaySponsor(): string {
    return getUserDisplayInfo(this.project?.sponsor || ({} as UserDTO));
  }

  get displayManager(): string {
    return getUserDisplayInfo(this.project?.manager || ({} as UserDTO));
  }

  get displayType(): string {
    return projectTypeMapping[this.project?.projectType || ''];
  }

  get displayStartDate(): string {
    if (this.project?.startDate) {
      return moment(this.project?.startDate).format(DATE_FORMAT.DISPLAY);
    }
    return DEFAULT_DISPLAY_VALUE.DATE;
  }

  get displayEndDate(): string {
    if (this.project?.endDate) {
      return moment(this.project?.endDate).format(DATE_FORMAT.DISPLAY);
    }
    return DEFAULT_DISPLAY_VALUE.DATE;
  }

  get membersData(): MembersData {
    if (this.project) {
      const projectAssessmentActive = this.project.projectAssessments.find(
        projectAssessment => projectAssessment.isActive
      );
      const members = sortBy(projectAssessmentActive?.userEngagements, 'user.fullName') || [];
      return {
        members,
        displayMembers: members.slice(0, numberOfDisplayMember),
        tooltip: members.length ? members.map(member => member.user.fullName).join(', ') : '0 members',
        numberOfMembers: members.length,
        numberOfNotDisplayMembers: members.length > numberOfDisplayMember ? members.length - numberOfDisplayMember : 0
      };
    }

    return {
      members: [],
      displayMembers: [],
      tooltip: '0 members',
      numberOfMembers: 0,
      numberOfNotDisplayMembers: 0
    } as MembersData;
  }

  async navigateToProjectEvaluation(): Promise<void> {
    await this.$router.push({
      name: PAGE_NAMES.PROJECT_EVALUATION,
      params: {
        id: '' + this.project.id
      }
    });
  }
}
